import { useRouter } from 'next/router';

import { dehydrate, QueryClient, useQuery } from 'react-query';
import { queryClientConfig } from '@core/configs/queryClientConfig';

import { NextSeo } from 'next-seo';
import { getReviewsSeoProps } from 'router/seoprops';

import { GetReviews, GetReviewsSummary } from 'services/core';

import Spinner from '@core/components/spinner/Spinner';
import ReviewsHero from 'components/pages/reviews/ReviewsHero';
import ReviewsCountries from 'components/pages/reviews/ReviewsCountries';
import ReviewsList from 'components/pages/reviews/ReviewsList';

import classes from './index.module.scss';

const PAGE_SIZE = 18;

const Reviews = () => {
	const router = useRouter();
	const page = router.query.page ? String(router.query.page) : '1';

	const seoProps = getReviewsSeoProps();

	const { data: summaryData, isLoading: isSummaryLoading } = useQuery(
		['reviews-summary'],
		GetReviewsSummary
	);
	const {
		data: reviewsData,
		isLoading: isReviewsLoading,
		isPreviousData
	} = useQuery(['reviews', page], () => GetReviews(`page=${page}&page_size=${PAGE_SIZE}`), {
		keepPreviousData: true
	});

	const summary = summaryData?.data;
	const reviews = reviewsData?.data;

	return (
		<div className={classes['page']}>
			<NextSeo {...seoProps} />
			<ReviewsHero summary={summary} isLoading={isSummaryLoading} />
			<div className={`container ${classes['content']}`}>
				{isSummaryLoading ? (
					<div className={classes['loading']}>
						<Spinner />
					</div>
				) : (
					summary && <ReviewsCountries summary={summary} />
				)}
				<ReviewsList
					reviews={reviews?.results}
					count={reviews?.count}
					pageSize={PAGE_SIZE}
					isLoading={isReviewsLoading || isPreviousData}
				/>
			</div>
		</div>
	);
};

export async function getServerSideProps(context) {
	const queryClient = new QueryClient(queryClientConfig);
	const page = context.query.page ? String(context.query.page) : '1';

	try {
		const [summaryResponse, reviewsResponse] = await Promise.all([
			GetReviewsSummary(),
			GetReviews(`page=${page}&page_size=${PAGE_SIZE}`)
		]);
		await queryClient.prefetchQuery(['reviews-summary'], async () => ({
			data: summaryResponse.data
		}));
		await queryClient.prefetchQuery(['reviews', page], async () => ({
			data: reviewsResponse.data
		}));
	} catch (e) {
		console.error('Error fetching reviews:', e);
	}

	return {
		props: {
			dehydratedState: dehydrate(queryClient)
		}
	};
}

export default Reviews;
